import { financialScope } from '../utils/financialScope';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Job {
  id: string;
  job_type: string;
  pipeline_stage_name: string;
  square_footage: number;
  sale_price_per_sf: number;
  cost_per_sf: number;
  total_revenue: number;
  total_cost: number;
  gross_profit: number;
  net_profit: number;
  salesman_commission: number;
  deposit_paid: boolean;
  approved_amount: number;
  total_price: number;
  estimate_id: string | null;
  homeowner_name: string;
  address: string;
  leads: { homeowner_name: string; address: string; };
  estimates: { total_sf: number; price_per_sf: number; cost_per_sf: number; salesman_commission: number; } | null;
  created_at: string;
}

const COST_PER_SF: Record<string, number> = { asphalt: 4, concrete: 8 };

const fmt$ = (n: number) => `$${Math.round(n).toLocaleString()}`;
const fmt$dec = (n: number) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ─── Shared Components ─────────────────────────────────────────────────────

const StatCard = ({ label, value, color, sub }: any) => (
  <div style={{ background: '#1A2B3C', borderRadius: 14, padding: 20, border: '1px solid #2D4259' }}>
    <div style={{ color: '#8FA8C0', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{label}</div>
    <div style={{ color: color || '#fff', fontSize: 28, fontWeight: 800 }}>{value}</div>
    {sub && <div style={{ color: '#4A6580', fontSize: 12, marginTop: 4 }}>{sub}</div>}
  </div>
);

const SectionCard: React.FC<{ title: string; children: React.ReactNode; right?: React.ReactNode }> = ({ title, children, right }) => (
  <div style={{ background: '#1A2B3C', borderRadius: 16, border: '1px solid #2D4259', overflow: 'hidden' }}>
    <div style={{ padding: '14px 20px', borderBottom: '1px solid #2D4259', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <h2 style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: 0 }}>{title}</h2>
      {right}
    </div>
    <div>{children}</div>
  </div>
);

// ─── Main Page ─────────────────────────────────────────────────────────────

type Tab = 'receivables' | 'pnl';

export default function FinancialsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('receivables');
  const [receivables, setReceivables] = useState<any>(null);
  const [loadingReceivables, setLoadingReceivables] = useState(true);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [roofingFinancials, setRoofingFinancials] = useState<Record<string, any>>({});
  const [loadingPnl, setLoadingPnl] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [pnlLoaded, setPnlLoaded] = useState(false);

  // Load receivables on mount (cheap, one endpoint)
  useEffect(() => {
    api.get('/dashboard/receivables')
      .then(r => setReceivables(r.data))
      .catch(e => console.error('Receivables load error:', e))
      .finally(() => setLoadingReceivables(false));
  }, []);

  // Lazy-load P&L data only when the tab opens
  useEffect(() => {
    if (tab !== 'pnl' || pnlLoaded) return;
    setLoadingPnl(true);
    const loadPnl = async () => {
      try {
        const allJobs = financialScope((await api.get('/jobs')).data).jobs;
        const estimateIds = allJobs.map((j: Job) => j.estimate_id).filter((id): id is string => Boolean(id));
        let enrichedJobs = allJobs;
        if (estimateIds.length > 0) {
          const results = await Promise.all(estimateIds.map((eid: string) => api.get(`/estimates/${eid}`).catch(() => null)));
          const estimateMap: Record<string, any> = {};
          results.forEach((r: any) => { if (r?.data) estimateMap[r.data.id] = r.data; });
          enrichedJobs = allJobs.map((j: Job) => j.estimate_id ? { ...j, estimates: estimateMap[j.estimate_id] || null } : j);
        }
        const roofingJobs = enrichedJobs.filter((j: any) => j.job_type === 'roofing' || !j.job_type);
        const financialsResults = await Promise.all(
          roofingJobs.map(async (j: any) => {
            try {
              const res = await api.get(`/financials/${j.id}`);
              return { id: j.id, data: res.data };
            } catch { return { id: j.id, data: null }; }
          })
        );
        const financialsMap: Record<string, any> = {};
        financialsResults.forEach(({ id, data }: any) => { if (data) financialsMap[id] = data; });
        const jobsWithFinancials = enrichedJobs.map((j: any) =>
          financialsMap[j.id] ? { ...j, _financials: financialsMap[j.id] } : j
        );
        setRoofingFinancials(financialsMap);
        setJobs(jobsWithFinancials);
        setPnlLoaded(true);
      } catch (e) { console.error('P&L load error:', e); }
      finally { setLoadingPnl(false); }
    };
    loadPnl();
  }, [tab, pnlLoaded]);

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ color: '#fff', fontSize: 24, fontWeight: 800, margin: 0 }}>Financials</h1>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #2D4259' }}>
        {[
          ['receivables', '💰 Receivables & Cash Position'],
          ['pnl', '📊 Job P&L'],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key as Tab)}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '10px 18px',
              cursor: 'pointer',
              color: tab === key ? '#fff' : '#8FA8C0',
              fontSize: 14,
              fontWeight: tab === key ? 700 : 500,
              borderBottom: tab === key ? '2px solid #4A7EB5' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'receivables' && (
        <ReceivablesTab
          data={receivables}
          loading={loadingReceivables}
          onJobClick={(id: string) => navigate(`/pipeline/${id}`)}
        />
      )}

      {tab === 'pnl' && (
        <PnlTab
          jobs={jobs}
          roofingFinancials={roofingFinancials}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          loading={loadingPnl}
          onJobClick={(id: string) => navigate(`/pipeline/${id}`)}
          onDeleteJob={async (id: string) => {
            if (!window.confirm('Delete this job? This cannot be undone.')) return;
            try {
              await api.delete(`/jobs/${id}`);
              setJobs(prev => prev.filter(j => j.id !== id));
            } catch { alert('Failed to delete job'); }
          }}
        />
      )}
    </div>
  );
}

// ─── Receivables Tab ───────────────────────────────────────────────────────

const ReceivablesTab: React.FC<{ data: any; loading: boolean; onJobClick: (id: string) => void }> = ({ data, loading, onJobClick }) => {
  if (loading) return <div style={{ color: '#8FA8C0', padding: 40, textAlign: 'center' }}>Loading receivables…</div>;
  if (!data) return <div style={{ color: '#EF4444', padding: 40, textAlign: 'center' }}>Failed to load receivables data.</div>;

  const totalPipeline = (data.pipeline_value.locked || 0) + (data.pipeline_value.imminent || 0) + (data.pipeline_value.in_progress || 0) + (data.pipeline_value.collecting || 0);

  return (
    <>
      {/* Top-line stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 20 }}>
        <StatCard label="Total Outstanding" value={fmt$(data.total_outstanding)} color="#EF4444" sub={`${data.open_invoice_count} open invoices`} />
        <StatCard label="Collected This Month" value={fmt$(data.collected.this_month)} color="#10B981"
          sub={data.collected.delta_pct === null ? 'first month with data' : `${data.collected.delta_pct >= 0 ? '↗' : '↘'} ${Math.abs(data.collected.delta_pct).toFixed(0)}% vs last month`} />
        <StatCard label="Locked Pipeline" value={fmt$(totalPipeline)} color="#4A7EB5" sub="Signed → Pending Payment" />
        <StatCard label="Coverage Exposure" value={fmt$(data.coverage.total_exposure)} color={data.coverage.total_exposure > 0 ? '#F59E0B' : '#10B981'}
          sub={data.coverage.underpaid_jobs.length ? `${data.coverage.underpaid_jobs.length} underpaid jobs` : 'All jobs covered'} />
      </div>

      {/* Row 1: Outstanding by type + Aging */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <SectionCard title="💵 Outstanding Invoice Balances">
          <div style={{ padding: '16px 20px' }}>
            {[
              ['Invoice balances', data.outstanding_by_type.invoice, '#4A7EB5'],
            ].map(([label, val, color]: any) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #2D425944' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 4, background: color }} />
                  <span style={{ color: '#fff', fontSize: 14 }}>{label}</span>
                </div>
                <span style={{ color: val > 0 ? '#fff' : '#4A6580', fontSize: 15, fontWeight: 700 }}>{val > 0 ? fmt$(val) : '—'}</span>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="⏳ A/R Aging">
          <div style={{ padding: '16px 20px' }}>
            {[
              { label: '0–30 days (Current)', value: data.aging.current, color: '#10B981', emoji: '🟢' },
              { label: '31–60 days', value: data.aging.thirty, color: '#F59E0B', emoji: '🟡' },
              { label: '61–90 days', value: data.aging.sixty, color: '#EF4444', emoji: '🟠' },
              { label: '90+ days (Critical)', value: data.aging.ninety_plus, color: '#DC2626', emoji: '🔴' },
            ].map(b => (
              <div key={b.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #2D425944' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span>{b.emoji}</span>
                  <span style={{ color: '#fff', fontSize: 14 }}>{b.label}</span>
                </div>
                <span style={{ color: b.value > 0 ? b.color : '#4A6580', fontSize: 15, fontWeight: 700 }}>{b.value > 0 ? fmt$(b.value) : '—'}</span>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Row 2: Top stale receivables */}
      <div style={{ marginBottom: 20 }}>
        <SectionCard title="🚨 Top Stale Receivables" right={<span style={{ color: '#8FA8C0', fontSize: 12 }}>{data.top_stale.length} oldest</span>}>
          {data.top_stale.length === 0 ? (
            <div style={{ color: '#4A6580', padding: 30, textAlign: 'center' }}>🎉 No outstanding receivables</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #2D4259' }}>
                  {['Homeowner', 'Job #', 'Stage', 'Owed', 'Primary Type', 'Days Stale', 'Bucket'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#4A6580', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.top_stale.map((r: any) => {
                  const bucketColor = r.aging_bucket === 'ninety_plus' ? '#DC2626' : r.aging_bucket === 'sixty' ? '#EF4444' : r.aging_bucket === 'thirty' ? '#F59E0B' : '#10B981';
                  const bucketLabel = r.aging_bucket === 'ninety_plus' ? '90+d' : r.aging_bucket === 'sixty' ? '61–90d' : r.aging_bucket === 'thirty' ? '31–60d' : '0–30d';
                  return (
                    <tr key={r.invoice_id}
                      onClick={() => onJobClick(r.job_id)}
                      style={{ borderBottom: '1px solid #2D4259', cursor: 'pointer' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#243447')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={{ padding: '12px 16px', color: '#fff', fontWeight: 600 }}>{r.homeowner_name}</td>
                      <td style={{ padding: '12px 16px', color: '#8FA8C0', fontSize: 12 }}>{r.job_number || '—'}</td>
                      <td style={{ padding: '12px 16px', color: '#8FA8C0', fontSize: 12 }}>{r.pipeline_stage || '—'}</td>
                      <td style={{ padding: '12px 16px', color: '#EF4444', fontWeight: 700 }}>{fmt$dec(r.amount_owed)}</td>
                      <td style={{ padding: '12px 16px', color: '#F59E0B', fontSize: 13 }}>{r.primary_type}</td>
                      <td style={{ padding: '12px 16px', color: '#fff', fontWeight: 600 }}>{r.days_stale}d</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ background: bucketColor + '22', color: bucketColor, padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, border: `1px solid ${bucketColor}44` }}>{bucketLabel}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </SectionCard>
      </div>

      {/* Row 3: Pipeline funnel + Collections trend */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <SectionCard title="🔄 Pipeline Value (Money in the Machine)">
          <div style={{ padding: '16px 20px' }}>
            {[
              { key: 'locked', label: 'Signed Contract → Material Order', color: '#4A7EB5' },
              { key: 'imminent', label: 'Material Order → Dumpster', color: '#8B5CF6' },
              { key: 'in_progress', label: 'Job In Progress → Complete', color: '#F59E0B' },
              { key: 'collecting', label: 'Final Invoice → Pending Payment', color: '#10B981' },
            ].map(bucket => {
              const val = data.pipeline_value[bucket.key] || 0;
              const count = data.pipeline_job_counts[bucket.key] || 0;
              const pct = totalPipeline > 0 ? (val / totalPipeline) * 100 : 0;
              return (
                <div key={bucket.key} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{bucket.label}</span>
                    <span style={{ color: bucket.color, fontSize: 13, fontWeight: 700 }}>{fmt$(val)} <span style={{ color: '#4A6580', fontWeight: 500 }}>({count})</span></span>
                  </div>
                  <div style={{ height: 8, background: '#243447', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: bucket.color, transition: 'width 0.3s' }} />
                  </div>
                </div>
              );
            })}
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #2D4259', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#8FA8C0', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Total in flight</span>
              <span style={{ color: '#fff', fontSize: 16, fontWeight: 800 }}>{fmt$(totalPipeline)}</span>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="📈 Collections This Month by Type">
          <div style={{ padding: '16px 20px' }}>
            {[
              ['Deposit', data.collected.by_type_this_month.acv, '#4A7EB5'],
              ['Deductibles', data.collected.by_type_this_month.deductible, '#EC4899'],
              ['Final Payment', data.collected.by_type_this_month.depreciation, '#F59E0B'],
              ['Supplement', data.collected.by_type_this_month.supplement, '#10B981'],
              ['Other', data.collected.by_type_this_month.other, '#8B5CF6'],
            ].filter(([, val]: any) => val > 0).map(([label, val, color]: any) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #2D425944' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 4, background: color }} />
                  <span style={{ color: '#fff', fontSize: 14 }}>{label}</span>
                </div>
                <span style={{ color: '#10B981', fontSize: 15, fontWeight: 700 }}>{fmt$dec(val)}</span>
              </div>
            ))}
            {data.collected.this_month === 0 && (
              <div style={{ color: '#4A6580', padding: 20, textAlign: 'center', fontSize: 13 }}>No collections this month yet</div>
            )}
          </div>
        </SectionCard>
      </div>

      {/* Row 4: Coverage exposure + Deductible nudges */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <SectionCard title="⚠️ Coverage Exposure" right={<span style={{ color: '#8FA8C0', fontSize: 12 }}>{data.coverage.underpaid_jobs.length} jobs</span>}>
          {data.coverage.underpaid_jobs.length === 0 ? (
            <div style={{ color: '#10B981', padding: 30, textAlign: 'center' }}>No cost shortfalls identified for invoiced jobs</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #2D4259' }}>
                  {['Homeowner', 'Invoice total', 'Est. Cost', 'Gap'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#4A6580', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.coverage.underpaid_jobs.map((j: any) => (
                  <tr key={j.job_id}
                    onClick={() => onJobClick(j.job_id)}
                    style={{ borderBottom: '1px solid #2D4259', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#243447')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ padding: '10px 16px', color: '#fff', fontSize: 13, fontWeight: 600 }}>{j.homeowner_name}</td>
                    <td style={{ padding: '10px 16px', color: '#10B981', fontSize: 13 }}>{fmt$(j.rcv)}</td>
                    <td style={{ padding: '10px 16px', color: '#F59E0B', fontSize: 13 }}>{fmt$(j.estimated_cost)}</td>
                    <td style={{ padding: '10px 16px', color: '#EF4444', fontWeight: 700 }}>−{fmt$(j.gap)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>

        <SectionCard title="⏰ Deductible Nudge Candidates" right={<span style={{ color: '#8FA8C0', fontSize: 12 }}>Sent &gt;7 days ago, unpaid</span>}>
          {data.deductible_nudges.length === 0 ? (
            <div style={{ color: '#10B981', padding: 30, textAlign: 'center' }}>✅ All deductible links current or paid</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #2D4259' }}>
                  {['Homeowner', 'Amount', 'Sent', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#4A6580', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.deductible_nudges.map((n: any) => (
                  <tr key={n.job_id}
                    style={{ borderBottom: '1px solid #2D4259' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#243447')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ padding: '10px 16px', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }} onClick={() => onJobClick(n.job_id)}>{n.homeowner_name}</td>
                    <td style={{ padding: '10px 16px', color: '#EC4899', fontWeight: 700 }}>{fmt$(n.amount)}</td>
                    <td style={{ padding: '10px 16px', color: '#F59E0B', fontSize: 13, fontWeight: 600 }}>{n.days_since_sent}d ago</td>
                    <td style={{ padding: '10px 16px' }}>
                      {n.payment_link && (
                        <a href={n.payment_link} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                          style={{ background: '#4A7EB522', border: '1px solid #4A7EB544', color: '#4A7EB5', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}>
                          Copy Link ↗
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>
      </div>
    </>
  );
};

// ─── P&L Tab (existing view, extracted) ─────────────────────────────────────

const PnlTab: React.FC<any> = ({ jobs, roofingFinancials, typeFilter, setTypeFilter, loading, onJobClick, onDeleteJob }) => {
  const filtered = jobs.filter((j: Job) => typeFilter === 'all' || (j.job_type || 'roofing') === typeFilter);
  const CLOSED_STAGES = ['Paid and Closed', 'Commission Paid', 'Closed'];
  const closed = filtered.filter((j: Job) => CLOSED_STAGES.includes(j.pipeline_stage_name));
  const allActive = filtered;

  const getJobFinancials = (j: Job) => {
    const isAC = ['asphalt', 'concrete'].includes(j.job_type);
    const est = (j as any).estimates;
    if (!isAC) {
      const rf = (j as any)._financials || roofingFinancials[(j as any).id];
      const financials = rf?.financials;
      const expenses: any[] = rf?.expenses || [];
      const squares = parseFloat(financials?.squares || 0);
      const revenue = Number((j as any).job_value || 0);
      const hasLabor = expenses.some((e: any) => e.type === 'labor');
      const hasMaterials = expenses.some((e: any) => e.type === 'materials');
      const laborCost = !hasLabor && squares ? squares * 110 : expenses.filter((e: any) => e.type === 'labor').reduce((s: number, e: any) => s + parseFloat(e.amount || 0), 0);
      const actualMaterialCost = parseFloat(financials?.material_order_total || 0);
      const materialCost = !hasMaterials ? (actualMaterialCost > 0 ? actualMaterialCost : (squares ? squares * 200 : 0)) : expenses.filter((e: any) => e.type === 'materials').reduce((s: number, e: any) => s + parseFloat(e.amount || 0), 0);
      const dumpsterCost = expenses.filter((e: any) => e.type === 'dumpster').reduce((s: number, e: any) => s + parseFloat(e.amount || 0), 0);
      const otherCosts = expenses.filter((e: any) => !['labor','materials','dumpster'].includes(e.type)).reduce((s: number, e: any) => s + parseFloat(e.amount || 0), 0);
      const commission = 300;
      const cost = laborCost + materialCost + dumpsterCost + otherCosts;
      const profit = revenue > 0 ? revenue - cost - commission : 0;
      return { revenue, cost, commission, profit, squares };
    }
    const revenue = Number((j as any).job_value || 0);
    const costPerSf = (est?.cost_per_sf) || COST_PER_SF[j.job_type] || 0;
    const cost = est && est.total_sf ? est.total_sf * costPerSf : 0;
    const commission = est?.salesman_commission || j.salesman_commission || 0;
    const profit = revenue > 0 ? revenue - cost - commission : 0;
    return { revenue, cost, commission, profit, squares: 0 };
  };

  const totalRevenue = allActive.reduce((sum: number, j: Job) => sum + getJobFinancials(j).revenue, 0);
  const totalCost = allActive.reduce((sum: number, j: Job) => sum + getJobFinancials(j).cost, 0);
  const totalProfit = allActive.reduce((sum: number, j: Job) => sum + getJobFinancials(j).profit, 0);
  const totalCommissions = allActive.reduce((sum: number, j: Job) => sum + getJobFinancials(j).commission, 0);
  const avgMargin = totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100).toFixed(1) : '0';

  const isClosedOrPaidStage = (stageName?: string) => {
    const stage = (stageName || '').toLowerCase();
    return stage.includes('closed') || stage.includes('payment collected') || stage.includes('paid');
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 20 }}>
        {[['all', 'All'], ['roofing', '🏠 Roofing'], ['asphalt', '🛣️ Asphalt'], ['concrete', '🪨 Concrete']].map(([key, label]) => (
          <button key={key} onClick={() => setTypeFilter(key)}
            style={{ padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: typeFilter === key ? '#4A7EB5' : '#1A2B3C',
              color: typeFilter === key ? '#fff' : '#8FA8C0',
              border: `1px solid ${typeFilter === key ? '#4A7EB5' : '#2D4259'}` }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 20 }}>
        <StatCard label="Total Revenue" value={fmt$(totalRevenue)} color="#10B981" sub={`${allActive.length} jobs`} />
        <StatCard label="Total Cost" value={fmt$(totalCost)} color="#EF4444" />
        <StatCard label="Gross Profit" value={fmt$(totalProfit)} color="#10B981" />
        <StatCard label="Avg Margin" value={`${avgMargin}%`} color={parseFloat(avgMargin) > 30 ? '#10B981' : '#F59E0B'} />
        <StatCard label="Commissions" value={fmt$(totalCommissions)} color="#F59E0B" />
        <StatCard label="Closed Jobs" value={closed.length} color="#4A7EB5" sub={`${fmt$(closed.reduce((s: number, j: Job) => s + getJobFinancials(j).revenue, 0))} collected`} />
      </div>

      <div style={{ background: '#1A2B3C', borderRadius: 16, border: '1px solid #2D4259', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #2D4259', display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: '#fff', fontWeight: 700 }}>All Jobs</span>
          <span style={{ color: '#8FA8C0', fontSize: 13 }}>{filtered.length} jobs</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #2D4259' }}>
              {['Type', 'Homeowner', 'Stage', 'Sq Ft', 'Revenue', 'Cost', 'Net Profit', 'Margin', 'Commission', 'Actions'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#4A6580', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((job: Job) => {
              const isAC = ['asphalt', 'concrete'].includes(job.job_type);
              const est = (job as any).estimates;
              const { revenue, cost, commission, profit, squares: roofingSquares } = getJobFinancials(job);
              const margin = revenue > 0 ? ((profit / revenue) * 100).toFixed(0) : '—';
              const sqft = isAC ? (est && est.total_sf) : (roofingSquares || null);
              const typeIcon = job.job_type === 'asphalt' ? '🛣️' : job.job_type === 'concrete' ? '🪨' : '🏠';
              const displayName = job.leads?.homeowner_name || job.homeowner_name;
              const canDelete = !isClosedOrPaidStage(job.pipeline_stage_name);
              return (
                <tr key={job.id} onClick={() => onJobClick(job.id)}
                  style={{ borderBottom: '1px solid #2D4259', cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#243447')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ padding: '12px 16px', fontSize: 18 }}>{typeIcon}</td>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: '#fff' }}>{displayName}</td>
                  <td style={{ padding: '12px 16px', color: '#8FA8C0', fontSize: 12 }}>{job.pipeline_stage_name}</td>
                  <td style={{ padding: '12px 16px', color: '#8FA8C0' }}>{sqft ? `${sqft}${isAC ? ' SF' : ' sq'}` : '—'}</td>
                  <td style={{ padding: '12px 16px', color: '#10B981', fontWeight: 700 }}>{revenue ? fmt$(revenue) : '—'}</td>
                  <td style={{ padding: '12px 16px', color: '#EF4444' }}>{cost ? fmt$(cost) : '—'}</td>
                  <td style={{ padding: '12px 16px', color: profit > 0 ? '#10B981' : '#EF4444', fontWeight: 700 }}>{profit ? fmt$(profit) : '—'}</td>
                  <td style={{ padding: '12px 16px' }}>{margin !== '—' ? <span style={{ color: parseInt(margin) > 30 ? '#10B981' : '#F59E0B', fontWeight: 700 }}>{margin}%</span> : '—'}</td>
                  <td style={{ padding: '12px 16px', color: '#F59E0B' }}>{commission ? `$${commission}` : '—'}</td>
                  <td style={{ padding: '12px 16px' }}>
                    {canDelete && (
                      <button onClick={(evt) => { evt.stopPropagation(); onDeleteJob(job.id); }} title="Delete job"
                        style={{ border: '1px solid #475569', background: '#0f172a', color: '#e2e8f0', borderRadius: 8, padding: '4px 8px', cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && !loading && <div style={{ padding: 60, textAlign: 'center', color: '#4A6580' }}>No jobs yet</div>}
        {loading && <div style={{ padding: 60, textAlign: 'center', color: '#8FA8C0' }}>Loading job P&L…</div>}
      </div>
    </>
  );
};
